/**
 * Offhoo App shell detection + native bridge helpers.
 * Keep OffhooApp.isInApp / applyShellClass / getAppPlatform / isAppIosPlatform
 * in sync with snippets/offhoo-app-boot.liquid.
 */
(function (global) {
    const root = global.OffhooApp || (global.OffhooApp = {});
    const CART_LINES_UPDATE = 'shopify:cart:lines-update';
    const BADGE_TAB_TYPE = 'cart';
    const ATT_PROMPTED_KEY = 'offhoo_att_prompted';
    const ATT_STATUS_KEY = 'offhoo_att_status';

    /**
     * @returns {boolean}
     */
    function isForceInAppValue(value) {
        return value === '1' || value === 'true';
    }

    /**
     * App-injected User-Agent: app_platform_ios | app_platform_android
     * @returns {'IOS'|'ANDROID'|''}
     */
    root.getAppPlatform = function getAppPlatform() {
        const ua = String(global.navigator?.userAgent || '');
        if (/app_platform_ios/i.test(ua)) return 'IOS';
        if (/app_platform_android/i.test(ua)) return 'ANDROID';
        return '';
    };

    /** @returns {boolean} */
    root.isAppIosPlatform = function isAppIosPlatform() {
        return root.getAppPlatform() === 'IOS';
    };

    root.isInApp = function isInApp() {
        const platform = root.getAppPlatform();
        if (platform === 'IOS' || platform === 'ANDROID') return true;
        if (global.flutter_inappwebview) return true;
        try {
            if (isForceInAppValue(localStorage.getItem('force_in_app'))) return true;
        } catch (_e) {
            /* ignore */
        }
        return false;
    };

    const APP_USAGE_STORAGE_PREFIX = 'offhoo_app_usage:';
    const APP_DEVICE_ID_KEY = 'app_device_id';
    const APP_USAGE_EVENT = 'offhoo_app_open';
    /** @type {Promise<boolean>|null} */
    let appUsageInflight = null;

    /**
     * App usage track → download-track API (event: offhoo_app_open).
     * App passes only device_id + platform (ios|android).
     * Each call: persist device_id to localStorage (key: app_device_id) if new/changed.
     * Lifetime dedupe (download-track only): localStorage device_id + customerId (or guest).
     *
     * @param {{ device_id?: string, platform?: string }} [options]
     * @returns {Promise<boolean>}
     */
    root.reportAppUsage = function reportAppUsage(options) {
        const opts = options || {};
        const deviceId = String(opts.device_id || '').trim();
        const platform = String(opts.platform || '').trim().toLowerCase();

        if (!deviceId) {
            console.warn('[OffhooApp] reportAppUsage: missing device_id');
            return Promise.resolve(false);
        }

        try {
            if (global.localStorage.getItem(APP_DEVICE_ID_KEY) !== deviceId) {
                global.localStorage.setItem(APP_DEVICE_ID_KEY, deviceId);
            }
        } catch (_e) {
            /* ignore */
        }
        try {
            if (typeof root.syncClientChannelToCart === 'function') {
                root.syncClientChannelToCart({ force: true });
            }
        } catch (_e) {
            /* ignore */
        }

        if (platform !== 'ios' && platform !== 'android') {
            console.warn('[OffhooApp] reportAppUsage: platform must be ios|android');
            return Promise.resolve(false);
        }

        const themeOffhoo = (global.Theme && global.Theme.offhoo) || {};
        const apiUrl = String(
            themeOffhoo.downloadTrackApiUrl ||
                (typeof themeOffhoo.joinApi === 'function'
                    ? themeOffhoo.joinApi('/api/v1/shopfiy/offhoo/download-track')
                    : '') ||
                ''
        ).trim();
        if (!apiUrl) {
            console.warn('[OffhooApp] reportAppUsage: api base / download-track URL not configured');
            return Promise.resolve(false);
        }

        const customerId = String(themeOffhoo.customerId || '').trim();
        const dedupeKey = `${APP_USAGE_STORAGE_PREFIX}${deviceId}|${customerId || 'guest'}`;

        try {
            if (global.localStorage.getItem(dedupeKey) === '1') {
                console.log('[OffhooApp] reportAppUsage: already marked', dedupeKey);
                return Promise.resolve(true);
            }
        } catch (_e) {
            /* ignore */
        }

        if (appUsageInflight) return appUsageInflight;

        const locale =
            (global.Shopify && global.Shopify.locale) ||
            (document.documentElement && document.documentElement.lang) ||
            '';

        const payload = {
            event: APP_USAGE_EVENT,
            phone_type: platform,
            platform,
            device: 'phone',
            locale: String(locale || ''),
            visitor_id: deviceId,
            page_url: String(global.location && global.location.href ? global.location.href : ''),
            consent_analytics: true,
            user_agent: String(global.navigator && global.navigator.userAgent ? global.navigator.userAgent : ''),
            ts: Date.now(),
        };
        if (customerId) payload.customer_id = customerId;

        appUsageInflight = fetch(apiUrl, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                Accept: 'application/json',
            },
            body: JSON.stringify(payload),
            credentials: 'omit',
            mode: 'cors',
            keepalive: true,
        })
            .then((res) => {
                if (!res.ok) {
                    console.warn('[OffhooApp] reportAppUsage failed', res.status);
                    return false;
                }
                try {
                    global.localStorage.setItem(dedupeKey, '1');
                } catch (_e) {
                    /* ignore */
                }
                return true;
            })
            .catch((err) => {
                console.warn('[OffhooApp] reportAppUsage error', err);
                return false;
            })
            .finally(() => {
                appUsageInflight = null;
            });

        return appUsageInflight;
    };

    /**
     * App → H5: native analytics report → OffhooAnalytics.trackAppEvent.
     * Examples:
     *   OffhooApp.reportAppTrack({ event: 'app_open', device_id: 'xxx' });
     *   OffhooApp.reportAppTrack({ event: 'button_click', device_id: 'xxx', button_name: 'language', button_id: 'de' });
     *   // → module_name forced to app_language
     *
     * @param {{
     *   event: 'page_view'|'button_click'|'app_open',
     *   module_name?: string,
     *   device_id?: string,
     *   button_id?: string,
     *   button_name?: string,
     * }} options
     * @returns {boolean}
     */
    root.reportAppTrack = function reportAppTrack(options) {
        const opts = options && typeof options === 'object' ? options : {};
        const deviceId = String(opts.device_id || '').trim();
        const eventName = String(opts.event || '').trim();

        console.log('[OffhooApp] reportAppTrack', opts);

        if (!eventName) {
            console.warn('[OffhooApp] reportAppTrack: missing event');
            return false;
        }

        if (deviceId) {
            try {
                if (global.localStorage.getItem(APP_DEVICE_ID_KEY) !== deviceId) {
                    global.localStorage.setItem(APP_DEVICE_ID_KEY, deviceId);
                }
            } catch (_e) {
                /* ignore */
            }
            try {
                if (typeof root.syncClientChannelToCart === 'function') {
                    root.syncClientChannelToCart({ force: true });
                }
            } catch (_e) {
                /* ignore */
            }
        }

        const trackOpts = {
            event: eventName,
            module_name: String(opts.module_name || '').trim(),
            device_id: deviceId,
            button_id: String(opts.button_id || '').trim(),
            button_name: String(opts.button_name || '').trim(),
        };

        const tryTrack = () => {
            const analytics = global.OffhooAnalytics;
            if (!analytics || typeof analytics.trackAppEvent !== 'function') return false;
            return Boolean(analytics.trackAppEvent(trackOpts));
        };

        if (tryTrack()) return true;

        // analytics.js is defer — retry briefly
        let attempts = 0;
        const maxAttempts = 5;
        const timer = global.setInterval(() => {
            attempts += 1;
            if (tryTrack() || attempts >= maxAttempts) {
                global.clearInterval(timer);
                if (attempts >= maxAttempts) {
                    console.warn('[OffhooApp] reportAppTrack: OffhooAnalytics not ready');
                }
            }
        }, 500);
        return true;
    };

    /**
     * Apply html markers used by offhoo-app-shell.css
     * Default mobile UI is App (new header). Browser gets is-offhoo-browser.
     */
    root.applyShellClass = function applyShellClass() {
        const html = document.documentElement;

        if (html.classList.contains('shopify-design-mode')) {
            html.classList.add('is-offhoo-browser');
            html.classList.remove('is-offhoo-app');
            html.setAttribute('data-offhoo-app', '0');
            return false;
        }

        const inApp = root.isInApp();

        if (inApp) {
            html.classList.add('is-offhoo-app');
            html.classList.remove('is-offhoo-browser');
            html.setAttribute('data-offhoo-app', '1');
        } else {
            html.classList.add('is-offhoo-browser');
            html.classList.remove('is-offhoo-app');
            html.setAttribute('data-offhoo-app', '0');
        }

        try {
            const platform = root.getAppPlatform();
            if (platform) html.setAttribute('data-app-platform', platform);
            else html.removeAttribute('data-app-platform');
        } catch (_e) {
            /* ignore */
        }

        return inApp;
    };

    /** Release Shopify cookie banner (remove ATT pending gate). */
    root.releaseAttPendingBanner = function releaseAttPendingBanner() {
        try {
            document.documentElement.classList.remove('offhoo-att-pending');
        } catch (_e) {
            /* ignore */
        }
    };

    /**
     * ATT denied in App:
     * - hide auto cookie banner (offhoo-hide-cookie-banner)
     * - hide Marketing row in prefs (offhoo-hide-marketing-prefs)
     * - footer "manage cookies" prefs stay available
     * - silently set consent with marketing=false so Shopify stops auto-prompting
     */
    root.applyMarketingPrefsVisibility = function applyMarketingPrefsVisibility() {
        try {
            const html = document.documentElement;
            const inApp = root.isInApp();
            const isIosApp = inApp && root.isAppIosPlatform();
            const prompted = root.hasAttBeenPrompted();
            const status = root.getStoredAttStatus();
            // ATT is iOS-only; do not apply denied cookie policy on Android.
            const denied = isIosApp && prompted && status !== 'authorized';

            html.classList.toggle('offhoo-hide-marketing-prefs', denied);
            html.classList.toggle('offhoo-hide-cookie-banner', denied);

            if (denied) {
                root.bindAcceptAllConsentIntercept();
                if (!root.__attDeniedConsentApplied) {
                    root.__attDeniedConsentApplied = true;
                    root.setConsentExcludingMarketing();
                }
            }

            return denied;
        } catch (_e) {
            return false;
        }
    };

    /**
     * App + ATT already denied → Accept-all must not enable marketing.
     * @returns {boolean}
     */
    root.shouldForceNoMarketingConsent = function shouldForceNoMarketingConsent() {
        return (
            root.isInApp() &&
            root.isAppIosPlatform() &&
            root.hasAttBeenPrompted() &&
            root.getStoredAttStatus() !== 'authorized'
        );
    };

    /**
     * @param {(err?: Error|null) => void} callback
     */
    function loadCustomerPrivacyApi(callback) {
        try {
            if (global.Shopify?.customerPrivacy?.setTrackingConsent) {
                callback(null);
                return;
            }
            if (typeof global.Shopify?.loadFeatures !== 'function') {
                callback(new Error('Shopify.loadFeatures unavailable'));
                return;
            }
            global.Shopify.loadFeatures(
                [{ name: 'consent-tracking-api', version: '0.1' }],
                (error) => callback(error || null)
            );
        } catch (err) {
            callback(err instanceof Error ? err : new Error(String(err)));
        }
    }

    function hideShopifyConsentUi() {
        try {
            // Banner only — keep #shopify-pc__prefs for footer "manage cookies".
            ['#shopify-pc__banner', '.shopify-pc__banner__dialog'].forEach((sel) => {
                document.querySelectorAll(sel).forEach((node) => {
                    if (!(node instanceof HTMLElement)) return;
                    node.style.setProperty('display', 'none', 'important');
                });
            });
        } catch (_e) {
            /* ignore */
        }
    }

    /**
     * Scheme A: accept analytics + preferences, never marketing (ATT denied).
     * @param {(ok: boolean) => void} [done]
     */
    root.setConsentExcludingMarketing = function setConsentExcludingMarketing(done) {
        loadCustomerPrivacyApi((error) => {
            if (error) {
                console.warn('[OffhooApp] consent API load failed:', error);
                if (typeof done === 'function') done(false);
                return;
            }

            try {
                global.Shopify.customerPrivacy.setTrackingConsent(
                    {
                        marketing: false,
                        analytics: true,
                        preferences: true,
                        sale_of_data: false,
                    },
                    () => {
                        console.log('[OffhooApp] setConsentExcludingMarketing ok', {
                            marketing: false,
                            analytics: true,
                            preferences: true,
                            sale_of_data: false,
                        });
                        hideShopifyConsentUi();
                        if (typeof done === 'function') done(true);
                    }
                );
            } catch (err) {
                console.warn('[OffhooApp] setTrackingConsent failed:', err);
                if (typeof done === 'function') done(false);
            }
        });
    };

    /**
     * @param {EventTarget|null} target
     * @returns {{ matched: boolean, via: string, el: Element|null, text: string }}
     */
    function matchAcceptAllCookieControl(target) {
        const empty = { matched: false, via: '', el: null, text: '' };
        if (!target || !(target instanceof Element)) return empty;

        const knownSelectors = [
            '#shopify-pc__banner__btn-accept',
            '#shopify-pc__prefs__header-accept',
            '#shopify-pc__prefs__accept',
            '[data-action="accept-all"]',
        ];

        for (let i = 0; i < knownSelectors.length; i++) {
            const sel = knownSelectors[i];
            const known = target.closest(sel);
            if (known) {
                const text = String(known.textContent || '')
                    .replace(/\s+/g, ' ')
                    .trim();
                return { matched: true, via: `selector:${sel}`, el: known, text };
            }
        }

        const btn = target.closest(
            '#shopify-pc__banner button, #shopify-pc__banner a, #shopify-pc__prefs button, #shopify-pc__prefs a'
        );
        if (!btn) return empty;

        const text = String(btn.textContent || '')
            .replace(/\s+/g, ' ')
            .trim();
        const textLower = text.toLowerCase();
        if (/aceptar\s*todo|accept\s*all|accepter\s*tout|alle\s*akzeptieren|aceitar\s*tudo/.test(textLower)) {
            return { matched: true, via: 'text', el: btn, text };
        }

        return empty;
    }

    /**
     * Capture-phase intercept: Accept all → setTrackingConsent without marketing.
     */
    root.bindAcceptAllConsentIntercept = function bindAcceptAllConsentIntercept() {
        if (root.__acceptAllConsentInterceptBound) return;
        root.__acceptAllConsentInterceptBound = true;

        document.addEventListener(
            'click',
            (event) => {
                if (!root.shouldForceNoMarketingConsent()) return;

                const match = matchAcceptAllCookieControl(event.target);
                if (!match.matched) return;

                event.preventDefault();
                event.stopPropagation();
                if (typeof event.stopImmediatePropagation === 'function') {
                    event.stopImmediatePropagation();
                }

                console.log('[OffhooApp] Accept-all button clicked (intercept)', {
                    via: match.via,
                    text: match.text,
                    elId: match.el && match.el.id,
                    elClass: match.el && match.el.className,
                    attStatus: root.getStoredAttStatus(),
                });
                console.log('[OffhooApp] intercept Accept-all → consent without marketing');
                root.setConsentExcludingMarketing((ok) => {
                    console.log('[OffhooApp] Accept-all intercept finished', { ok });
                });
            },
            true
        );
    };

    /**
     * @returns {string} Stored ATT status or ''
     */
    root.getStoredAttStatus = function getStoredAttStatus() {
        try {
            return String(localStorage.getItem(ATT_STATUS_KEY) || '').trim();
        } catch (_e) {
            return '';
        }
    };

    /**
     * @returns {boolean}
     */
    root.hasAttBeenPrompted = function hasAttBeenPrompted() {
        try {
            return localStorage.getItem(ATT_PROMPTED_KEY) === '1';
        } catch (_e) {
            return false;
        }
    };

    /**
     * Persist ATT prompt + status. Prompted flag ensures ATT is shown only once.
     * @param {string} status
     */
    root.saveAttStatus = function saveAttStatus(status) {
        const normalized = normalizeAttStatus(status);
        try {
            localStorage.setItem(ATT_PROMPTED_KEY, '1');
            localStorage.setItem(ATT_STATUS_KEY, normalized);
        } catch (_e) {
            /* ignore */
        }
        root.applyMarketingPrefsVisibility();
        return normalized;
    };

    /**
     * Normalize App ATT callback. App returns `{ code: 0 | -1 }` only.
     * 0 = authorized, -1 = denied.
     * @param {unknown} raw
     * @returns {'authorized' | 'denied'}
     */
    function normalizeAttStatus(raw) {
        let code = raw;
        if (code && typeof code === 'object') {
            code = /** @type {Record<string, unknown>} */ (code).code;
        }

        const n = Number(code);
        if (n === 0) return 'authorized';
        return 'denied';
    }

    /**
     * Request iOS ATT via App messageChannel (EVENT / iosTrack). Once per install (localStorage).
     * @returns {Promise<string>} normalized status
     */
    root.requestIosAtt = function requestIosAtt() {
        return new Promise((resolve) => {
            if (root.hasAttBeenPrompted()) {
                resolve(root.getStoredAttStatus() || 'denied');
                return;
            }

            const type = 'EVENT';
            const action = 'iosTrack';
            const id = Date.now();
            const funcName = `${type}${action}${id}`;
            let settled = false;

            const finish = (rawStatus, source) => {
                console.log('iosTrack finish ...', source);

                if (settled) return;
                settled = true;

                let rawJson = '';
                try {
                    rawJson = JSON.stringify(rawStatus);
                } catch (_e) {
                    rawJson = String(rawStatus);
                }

                console.log('[OffhooApp] iosTrack raw return', {
                    source: source || 'unknown',
                    funcName,
                    typeofRaw: typeof rawStatus,
                    raw: rawStatus,
                    rawJson,
                    code:
                        rawStatus && typeof rawStatus === 'object'
                            ? /** @type {Record<string, unknown>} */ (rawStatus).code
                            : undefined,
                });

                const status = root.saveAttStatus(rawStatus);
                root.releaseAttPendingBanner();
                console.log('[OffhooApp] iosTrack normalized', { status, source: source || 'unknown' });
                resolve(status);

                try {
                    delete global[funcName];
                } catch (_e) {
                    /* ignore */
                }
            };

            const bridgeSnapshot = {
                hasFlutter: Boolean(global.flutter_inappwebview),
                hasCallHandler: Boolean(global.flutter_inappwebview?.callHandler),
                hasWebkitMessageChannel: Boolean(global.webkit?.messageHandlers?.messageChannel),
                hasOffhooAppBridge: Boolean(global.OffhooAppBridge?.postMessage),
                readyState: global.document?.readyState,
                time: Date.now(),
            };
            console.log('[OffhooApp] iosTrack before send — bridge status', bridgeSnapshot);

            global[funcName] = (data) => {
                finish(data, 'app-callback');
            };

            const message = {
                id,
                type,
                action,
                params: {},
            };

            const sent = root.postMessageChannel(message);
            console.log('[OffhooApp] iosTrack postMessageChannel', { sent, id, funcName, message });
            if (!sent) {
                finish('denied', 'send-failed');
            }
        });
    };

    /**
     * iOS App homepage only: gate cookie banner until ATT completes (once).
     * Cookie banner is already CSS-limited to homepage in App — see offhoo-app-shell.css.
     */
    let attGateStarted = false;
    root.bootIosAttGate = function bootIosAttGate() {
        const config = global.OffhooAppConfig || {};
        if (!root.isInApp()) return;
        if (!root.isAppIosPlatform()) {
            root.releaseAttPendingBanner();
            return;
        }
        if (config.templateName !== 'index') {
            root.releaseAttPendingBanner();
            return;
        }

        if (root.hasAttBeenPrompted()) {
            root.releaseAttPendingBanner();
            root.applyMarketingPrefsVisibility();
            return;
        }

        if (attGateStarted) return;
        attGateStarted = true;

        try {
            document.documentElement.classList.add('offhoo-att-pending');
        } catch (_e) {
            /* ignore */
        }

        console.log('[OffhooApp] bootIosAttGate start', {
            platform: root.getAppPlatform(),
            templateName: config.templateName,
            readyState: global.document?.readyState,
            hasCallHandler: Boolean(global.flutter_inappwebview?.callHandler),
        });

        root.requestIosAtt().catch(() => {
            root.saveAttStatus('denied');
            root.releaseAttPendingBanner();
        });
    };

    /**
     * Post a JSON message to the native App via messageChannel.
     * @param {object|string} message
     * @returns {boolean}
     */
    root.postMessageChannel = function postMessageChannel(message) {
        const payload = typeof message === 'string' ? message : JSON.stringify(message);

        try {
            if (global.flutter_inappwebview?.callHandler) {
                global.flutter_inappwebview.callHandler('messageChannel', payload);
                return true;
            }
            if (global.OffhooAppBridge?.postMessage) {
                global.OffhooAppBridge.postMessage(payload);
                return true;
            }
            if (global.webkit?.messageHandlers?.messageChannel) {
                const data = typeof message === 'string' ? JSON.parse(message) : message;
                global.webkit.messageHandlers.messageChannel.postMessage(data);
                return true;
            }
        } catch (err) {
            console.warn('[OffhooApp] postMessageChannel failed:', err);
        }
        return false;
    };

    /**
     * Same as postMessageChannel, but awaits Flutter callHandler return value when available.
     * Bridges without a return value resolve `{ ok: true, result: null }`.
     * @param {object|string} message
     * @returns {Promise<{ ok: boolean, result?: unknown, error?: unknown }>}
     */
    root.postMessageChannelAsync = function postMessageChannelAsync(message) {
        const payload = typeof message === 'string' ? message : JSON.stringify(message);

        try {
            if (global.flutter_inappwebview?.callHandler) {
                return Promise.resolve(global.flutter_inappwebview.callHandler('messageChannel', payload))
                    .then((result) => ({ ok: true, result }))
                    .catch((error) => ({ ok: false, error }));
            }
            if (global.OffhooAppBridge?.postMessage) {
                global.OffhooAppBridge.postMessage(payload);
                return Promise.resolve({ ok: true, result: null });
            }
            if (global.webkit?.messageHandlers?.messageChannel) {
                const data = typeof message === 'string' ? JSON.parse(message) : message;
                global.webkit.messageHandlers.messageChannel.postMessage(data);
                return Promise.resolve({ ok: true, result: null });
            }
        } catch (err) {
            console.warn('[OffhooApp] postMessageChannelAsync failed:', err);
            return Promise.resolve({ ok: false, error: err });
        }
        return Promise.resolve({ ok: false });
    };

    /**
     * History back/forward (incl. many system gesture backs) — skip load-time badge sync.
     * @returns {boolean}
     */
    function isBackForwardNavigation() {
        let navType = '';
        try {
            const nav = global.performance?.getEntriesByType?.('navigation')?.[0];
            if (nav && nav.type) navType = String(nav.type);
        } catch (_e) {
            /* ignore */
        }
        try {
            // Legacy PerformanceNavigation (Safari / older WebViews)
            if (!navType && global.performance?.navigation != null) {
                const legacy = Number(global.performance.navigation.type);
                if (legacy === 0) navType = 'navigate';
                else if (legacy === 1) navType = 'reload';
                else if (legacy === 2) navType = 'back_forward';
                else navType = `legacy:${legacy}`;
            }
        } catch (_e) {
            /* ignore */
        }

        console.log('[OffhooApp] navigation.type', navType || '(empty)', {
            template: (global.OffhooAppConfig || {}).templateName,
            suffix: (global.OffhooAppConfig || {}).templateSuffix,
        });

        return navType === 'back_forward';
    }

    /**
     * Pages that should push cart badge on first open / reload (not back_forward).
     * /collections => list-collections; user-center => page + user-center suffix.
     * @returns {boolean}
     */
    root.shouldSyncCartBadgeOnLoad = function shouldSyncCartBadgeOnLoad() {
        if (isBackForwardNavigation()) return false;

        const cfg = global.OffhooAppConfig || {};
        const name = cfg.templateName || '';
        const suffix = cfg.templateSuffix || '';

        if (name === 'index') return true;
        if (name === 'list-collections') return true;
        if (name === 'cart') return true;
        if (name === 'page' && suffix === 'user-center') return true;
        return false;
    };

    /**
     * Sync cart quantity badge to App Tab (tabType fixed as "cart").
     * @param {number|string} [count]
     * @returns {boolean}
     */
    root.syncCartBadge = function syncCartBadge(count) {
        if (!root.isInApp()) return false;

        let badgeCount = count;
        if (badgeCount == null || badgeCount === '') {
            const cfg = global.OffhooAppConfig || {};
            badgeCount = cfg.cartItemCount;
        }
        const badge = String(Math.max(0, Number(badgeCount) || 0));

        const cartMsg = {
            type: 'EVENT',
            action: 'badge',
            params: {
                tabType: BADGE_TAB_TYPE,
                badge,
            },
        };

        console.log('messageChannel_syncCartBadge', JSON.stringify(cartMsg));
        return root.postMessageChannel(cartMsg);
    };

    function readCountFromCartUpdateEvent(event) {
        return event?.promise
            ?.then(({ cart, detail }) => {
                const itemCount = cart?.totalQuantity ?? detail?.itemCount ?? 0;
                root.syncCartBadge(itemCount);
                if (global.OffhooAppConfig) {
                    global.OffhooAppConfig.cartItemCount = itemCount;
                }
            })
            .catch((error) => {
                if (error?.name !== 'AbortError') {
                    console.warn('[OffhooApp] cart badge update failed:', error);
                }
            });
    }

    function bindCartBadgeListeners() {
        // Live cart updates always sync. Load-time sync skips back_forward (see shouldSyncCartBadgeOnLoad).
        document.addEventListener(CART_LINES_UPDATE, readCountFromCartUpdateEvent);
    }

    /**
     * Ask App to switch to home Tab.
     * @returns {boolean}
     */
    root.openHomeTab = function openHomeTab() {
        if (!root.isInApp()) return false;

        const homeMsg = {
            type: 'PAGE',
            action: 'tab',
            params: { tabType: 'home' },
        };

        console.log('messageChannel_homeMsg', JSON.stringify(homeMsg));
        return root.postMessageChannel(homeMsg);
    };

    /**
     * Ask App to switch to category Tab (collections index).
     * @returns {boolean}
     */
    root.openCategoryTab = function openCategoryTab() {
        if (!root.isInApp()) return false;

        const categoryMsg = {
            type: 'PAGE',
            action: 'tab',
            params: { tabType: 'category' },
        };

        console.log('messageChannel_categoryMsg', JSON.stringify(categoryMsg));
        return root.postMessageChannel(categoryMsg);
    };

    /**
     * Ask App to switch to cart Tab.
     * @returns {boolean}
     */
    root.openCartTab = function openCartTab() {
        if (!root.isInApp()) return false;

        const cartMsg = {
            type: 'PAGE',
            action: 'tab',
            params: { tabType: 'cart' },
        };

        console.log('messageChannel_cartMsg', JSON.stringify(cartMsg));
        return root.postMessageChannel(cartMsg);
    };

    /**
     * Ask App to switch to mine / profile Tab.
     * @returns {boolean}
     */
    root.openMineTab = function openMineTab() {
        if (!root.isInApp()) return false;

        const mineMsg = {
            type: 'PAGE',
            action: 'tab',
            params: { tabType: 'mine' },
        };

        console.log('messageChannel_mineMsg', JSON.stringify(mineMsg));
        return root.postMessageChannel(mineMsg);
    };

    /**
     * Normalize App → H5 tab names.
     * @param {unknown} value
     * @returns {string}
     */
    function normalizeTabType(value) {
        const raw = String(value || '')
            .trim()
            .toLowerCase();
        if (!raw) return '';
        if (raw === 'profile' || raw === 'user' || raw === 'user_center' || raw === 'account') {
            return 'mine';
        }
        if (raw === 'category' || raw === 'categories' || raw === 'collection') {
            return 'category';
        }
        return raw;
    }

    /** Same tabType: ignore repeats within this window (rapid Tab taps). */
    const TAB_VISIBLE_THROTTLE_MS = 1000;
    /** @type {Record<string, number>} */
    const lastTabVisibleAt = Object.create(null);

    /**
     * App → H5: bottom Tab became visible (native TabBar switched to this WebView).
     * Call from App, e.g. Flutter:
     *   controller.evaluateJavascript(source: "window.OffhooApp.onTabVisible('mine');");
     *
     * Throttle: same tabType within 1s is ignored (not "already on tab" detection).
     *
     * @param {string} tabType  home | category | cart | mine
     * @param {object} [extra]
     * @returns {boolean}
     */
    root.onTabVisible = function onTabVisible(tabType, extra) {
        const normalized = normalizeTabType(tabType);
        if (!normalized) {
            console.warn('[OffhooApp] onTabVisible: missing tabType');
            return false;
        }

        const now = Date.now();
        const prev = lastTabVisibleAt[normalized] || 0;
        if (prev && now - prev < TAB_VISIBLE_THROTTLE_MS) {
            console.log('[OffhooApp] onTabVisible throttled', normalized, now - prev, 'ms');
            return false;
        }
        lastTabVisibleAt[normalized] = now;

        // tabType / time must win over App-passed extra.
        const detail = {
            ...(extra && typeof extra === 'object' ? extra : {}),
            tabType: normalized,
            time: now,
        };

        // Keep last detail so late listeners (analytics / user-center) can replay.
        root.__lastTabVisibleDetail = detail;

        console.log('[OffhooApp] onTabVisible', normalized);

        try {
            global.dispatchEvent(new CustomEvent('offhoo:tab-visible', { detail }));
        } catch (err) {
            console.warn('[OffhooApp] onTabVisible dispatch failed:', err);
            return false;
        }
        return true;
    };

    function bindTabTriggers() {
        document.addEventListener(
            'click',
            (event) => {
                const homeTrigger = event.target.closest('[data-offhoo-open-home-tab]');
                if (homeTrigger) {
                    if (!root.isInApp()) return;
                    event.preventDefault();
                    event.stopPropagation();
                    root.openHomeTab();
                    return;
                }

                const categoryTrigger = event.target.closest('[data-offhoo-open-category-tab]');
                if (categoryTrigger) {
                    if (!root.isInApp()) return;
                    event.preventDefault();
                    event.stopPropagation();
                    root.openCategoryTab();
                    return;
                }

                const cartTrigger = event.target.closest('[data-offhoo-open-cart-tab]');
                if (cartTrigger) {
                    if (!root.isInApp()) return;
                    event.preventDefault();
                    event.stopPropagation();
                    root.openCartTab();
                    return;
                }

                const mineTrigger = event.target.closest('[data-offhoo-open-mine-tab]');
                if (mineTrigger) {
                    if (!root.isInApp()) return;
                    event.preventDefault();
                    event.stopPropagation();
                    root.openMineTab();
                }
            },
            true
        );
    }

    function syncCartBadgeFromConfigWithRetry(count) {
        let attempts = 0;
        const maxAttempts = 5;

        const trySync = () => {
            attempts += 1;
            const ok = root.syncCartBadge(count);
            if (ok || attempts >= maxAttempts) return;
            global.setTimeout(trySync, 300 * attempts);
        };

        trySync();
    }

    function onPageShowForCartBadge(event) {
        if (!root.isInApp()) return;
        if (!event.persisted) return;
        if (!root.shouldSyncCartBadgeOnLoad()) return;
        syncCartBadgeFromConfigWithRetry();
    }

    function bootCartBadge() {
        bindTabTriggers();

        if (!root.isInApp()) return;

        bindCartBadgeListeners();
        global.addEventListener('pageshow', onPageShowForCartBadge);

        if (!root.shouldSyncCartBadgeOnLoad()) return;

        syncCartBadgeFromConfigWithRetry();
    }

    /**
     * Map current template → page_title style label (debug / future track).
     * @returns {string}
     */
    function getDebugPageTitle() {
        const cfg = global.OffhooAppConfig || {};
        const name = String(cfg.templateName || '');
        const suffix = String(cfg.templateSuffix || '');
        if (name === 'index') return 'homepage';
        if (name === 'list-collections') return 'category';
        if (name === 'cart') return 'cart';
        if (name === 'page' && suffix === 'user-center') return 'me';
        if (name === 'collection') return 'productlist';
        if (name === 'product') return 'productdetail';
        if (name === 'search') return 'searchresult';
        return name || '(unknown)';
    }

    function logPageView(source, extra) {
        const page = getDebugPageTitle();
        console.log('[OffhooApp] page_view', {
            source,
            page,
            templateName: (global.OffhooAppConfig || {}).templateName,
            templateSuffix: (global.OffhooAppConfig || {}).templateSuffix,
            url: String(global.location && global.location.href ? global.location.href : ''),
            ...(extra && typeof extra === 'object' ? extra : {}),
        });
    }

    /** Temporary: listen tab-visible + pageshow and print page for track testing. */
    function bindPageViewDebug() {
        global.addEventListener('pageshow', (event) => {
            logPageView('pageshow', { persisted: Boolean(event && event.persisted) });
        });
    }

    root.applyShellClass();

    function bootAfterDomReady() {
        root.applyShellClass();
        bootCartBadge();
        bindPageViewDebug();
        root.applyMarketingPrefsVisibility();
        root.bindAcceptAllConsentIntercept();
        root.bootIosAttGate();
    }

    if (global.document?.readyState === 'loading') {
        global.document.addEventListener('DOMContentLoaded', bootAfterDomReady);
    } else {
        bootAfterDomReady();
    }

})(typeof window !== 'undefined' ? window : globalThis);
